/**
 * Document Type Configuration Registry
 * 
 * This configuration drives all document type behavior:
 * - Display names
 * - Category mapping
 * - Normalization logic (including OCR summary passthrough)
 * - Display order
 * - Subtitle generation from OCR data
 * 
 * To add a new document type, simply add an entry here.
 * No code changes needed in Profile.js or hooks.
 */

import { maskValue } from '../components/UI/SensitiveFieldDisplay';

/**
 * Shared normalization base — every document gets these fields.
 * Individual configs can spread this + override / add fields.
 */
const baseNormalize = (doc, approvalType, category, fallbackName) => ({
  id: doc.id,
  name: doc.document_info?.filename?.split('/')?.pop() || doc.document_info?.filename || fallbackName,
  category,
  status: doc.status || 'uploaded',
  uploadedAt: doc.document_info?.uploaded_at || doc.submitted_at || null,
  size: 0,
  approvalType,
  ocrSummary: doc.ocr_summary || {},
  isProfileSourced: doc.source === 'profile_record',
  profileRecordId: doc.profile_record_id || null,
  profileRecordType: doc.profile_record_type || null,
  hasDocumentFile: Boolean(
    doc.has_document_file
    || (doc.document_info?.file_path || '').trim()
  ),
  filePath: (doc.document_info?.file_path || '').trim() || null,
  rejectionReason: doc.rejection_reason || '',
});

export const documentTypeConfig = {
  // Birth Certificate
  birth_certificate: {
    displayName: 'Birth Certificate',
    category: 'family',
    normalize: (doc) => baseNormalize(doc, 'birth_certificate', 'family', `birth_cert_${doc.id}`),
    displayOrder: 1
  },
  
  // Marriage Certificate
  marriage_certificate: {
    displayName: 'Marriage Certificate',
    category: 'family',
    normalize: (doc) => baseNormalize(doc, 'marriage_certificate', 'family', `marriage_cert_${doc.id}`),
    displayOrder: 2
  },
  
  // ID Card Front
  id_card_front: {
    displayName: 'ID Card (Front)',
    category: 'identity',
    singleton: true,
    normalize: (doc) => baseNormalize(doc, 'id_card_front', 'identity', `id_card_${doc.id}`),
    displayOrder: 3
  },
  
  // ID Card Back
  id_card_back: {
    displayName: 'ID Card (Back)',
    category: 'identity',
    singleton: true,
    normalize: (doc) => baseNormalize(doc, 'id_card_back', 'identity', `id_card_${doc.id}`),
    displayOrder: 4
  },
  
  // Education Certificate
  education_certificate: {
    displayName: 'Education Certificate',
    category: 'education',
    normalize: (doc) => baseNormalize(doc, 'education_certificate', 'education', `education_cert_${doc.id}`),
    displayOrder: 5
  },
  
  // Professional Certificate
  professional_certificate: {
    displayName: 'Professional Certificate',
    category: 'professional',
    normalize: (doc) => baseNormalize(doc, 'professional_certificate', 'professional', `professional_cert_${doc.id}`),
    displayOrder: 6
  },
  
  // Passport
  passport: {
    displayName: 'Passport',
    category: 'identity',
    normalize: (doc) => baseNormalize(doc, 'passport', 'identity', `passport_${doc.id}`),
    displayOrder: 7
  },

  // Driving Licence (Singapore)
  driving_license: {
    displayName: 'Driving Licence',
    category: 'identity',
    normalize: (doc) => baseNormalize(doc, 'driving_license', 'identity', `driving_licence_${doc.id}`),
    displayOrder: 7.25
  },

  // Bank Statement
  // NOTE: the backend `/document-approvals/all/` response does NOT include a
  // `category` field, so without this entry bank statements fall through to the
  // generic normaliser and get `category: 'other'`. The Banking dropzone filters
  // on `category === 'banking'`, so an 'other' document never matches — leaving
  // the card stuck on "Required" forever, even after HR approval.
  bank_statement: {
    displayName: 'Bank Statement',
    category: 'banking',
    normalize: (doc) => baseNormalize(doc, 'bank_statement', 'banking', `bank_statement_${doc.id}`),
    displayOrder: 7.5
  },
  
  // Medical Certificate
  medical_certificate: {
    displayName: 'Medical Certificate',
    category: 'medical',
    normalize: (doc) => ({
      ...baseNormalize(doc, 'medical_certificate', 'medical', `medical_cert_${doc.id}`),
      application_id: doc.leave_application_id || null,  // Required for MC verification
    }),
    displayOrder: 8
  },
  
  // Death Certificate
  death_certificate: {
    displayName: 'Death Certificate',
    category: 'family',
    normalize: (doc) => baseNormalize(doc, 'death_certificate', 'family', `death_cert_${doc.id}`),
    displayOrder: 9
  },
  
  // Adoption Papers
  adoption_papers: {
    displayName: 'Adoption Papers',
    category: 'family',
    normalize: (doc) => baseNormalize(doc, 'adoption_papers', 'family', `adoption_${doc.id}`),
    displayOrder: 10
  },
  
  // NS Call-up Order
  ns_call_up: {
    displayName: 'NS Call-up Order',
    category: 'military',
    normalize: (doc) => baseNormalize(doc, 'ns_call_up', 'military', `ns_callup_${doc.id}`),
    displayOrder: 11
  },

  // Non-OCR supporting document retained for employee/HR inspection.
  other_document: {
    displayName: 'Other Document',
    category: 'other',
    normalize: (doc) => baseNormalize(doc, 'other_document', 'other', `other_document_${doc.id}`),
    displayOrder: 12
  }
};

/**
 * Get display name for a document type
 * @param {string} documentType - The document type (e.g., 'birth_certificate', 'id_card_front')
 * @returns {string} Display name for the document type
 */
export const getDocumentDisplayName = (documentType) => {
  if (!documentType) return 'Document';
  
  // Check configuration first
  const configDisplayName = documentTypeConfig[documentType]?.displayName;
  if (configDisplayName) return configDisplayName;
  
  // Humanize unknown types: 'education_certificate' -> 'Education Certificate'
  return documentType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Field of study only earns a subtitle slot when it adds information
 * the qualification name does not already carry.
 * Mirrors staff_core.services.cv_data_aggregator_service._should_append_field_of_study.
 *
 * @param {string} qualificationName
 * @param {string} fieldOfStudy
 * @returns {boolean}
 */
export const shouldShowEducationFieldOfStudy = (qualificationName, fieldOfStudy) => {
  const field = (fieldOfStudy || '').trim();
  if (!field) return false;
  const qualification = (qualificationName || '').trim();
  if (!qualification) return true;
  return !qualification.toLowerCase().includes(field.toLowerCase());
};

/**
 * Get a meaningful one-line subtitle from OCR summary data.
 * Returns a human-readable string that distinguishes documents of the same type.
 *
 * @param {string} approvalType - The document approval type (e.g., 'education_certificate')
 * @param {Object} ocrSummary - OCR summary object from the backend
 * @param {string} filename - Original filename as last-resort fallback
 * @param {Object} [options]
 * @param {boolean} [options.omitQualification] - When the card title already is the qualification name
 * @returns {string|null} Subtitle string, or null if no meaningful data available
 */
export const getDocumentSubtitle = (approvalType, ocrSummary, filename, options = {}) => {
  if (!ocrSummary || typeof ocrSummary !== 'object' || Object.keys(ocrSummary).length === 0) {
    return null;
  }

  switch (approvalType) {
    case 'education_certificate': {
      const qualification = (ocrSummary.qualification_name || '').trim();
      const field = (ocrSummary.field_of_study || '').trim();
      const institution = (ocrSummary.institution_name || '').trim();
      const year = ocrSummary.end_year;
      const parts = [];
      if (!options.omitQualification && qualification) parts.push(qualification);
      if (shouldShowEducationFieldOfStudy(qualification, field)) parts.push(field);
      if (institution) parts.push(institution);
      if (year) parts.push(String(year));
      return parts.length > 0 ? parts.join(' · ') : null;
    }

    case 'professional_certificate': {
      const parts = [];
      if (ocrSummary.cert_name) parts.push(ocrSummary.cert_name);
      if (ocrSummary.issuing_organization) parts.push(ocrSummary.issuing_organization);
      return parts.length > 0 ? parts.join(' - ') : null;
    }

    case 'birth_certificate': {
      const parts = [];
      if (ocrSummary.child_full_name) parts.push(`Child: ${ocrSummary.child_full_name}`);
      if (ocrSummary.child_date_of_birth) parts.push(`(${ocrSummary.child_date_of_birth})`);
      return parts.length > 0 ? parts.join(' ') : null;
    }

    case 'marriage_certificate': {
      const names = [];
      if (ocrSummary.bridegroom_full_name) names.push(ocrSummary.bridegroom_full_name);
      if (ocrSummary.bride_full_name) names.push(ocrSummary.bride_full_name);
      const namePart = names.length === 2 ? names.join(' & ') : names.join('');
      if (namePart && ocrSummary.marriage_date) return `${namePart} (${ocrSummary.marriage_date})`;
      if (namePart) return namePart;
      if (ocrSummary.marriage_date) return `Married: ${ocrSummary.marriage_date}`;
      return null;
    }

    case 'id_card_front':
    case 'id_card_back': {
      const parts = [];
      if (ocrSummary.id_card_name) parts.push(ocrSummary.id_card_name);
      if (ocrSummary.id_card_nric_fin_number) parts.push(`NRIC: ${maskValue(ocrSummary.id_card_nric_fin_number, 'nric')}`);
      return parts.length > 0 ? parts.join(' - ') : null;
    }

    case 'bank_statement': {
      const parts = [];
      if (ocrSummary.ocr_bank_name) parts.push(ocrSummary.ocr_bank_name);
      if (ocrSummary.ocr_bank_account_number) parts.push(`Acct: ${maskValue(ocrSummary.ocr_bank_account_number, 'account')}`);
      return parts.length > 0 ? parts.join(' - ') : null;
    }

    // Medical certificate: intentionally no subtitle (sensitive)
    case 'medical_certificate':
      return null;

    default:
      return null;
  }
};

/**
 * Get category for a document type
 * @param {string} documentType - The document type
 * @returns {string} Category (e.g., 'family', 'identity')
 */
export const getDocumentCategory = (documentType) => {
  if (!documentType) return 'other';
  return documentTypeConfig[documentType]?.category || 'other';
};

export const SINGLETON_ID_CARD_TYPES = new Set(['id_card_front', 'id_card_back']);
export const HIDDEN_ID_CARD_STATUSES = new Set(['rejected', 'expired', 'superseded']);

/**
 * Keep at most one ID card front and one back per employee.
 * Other document types pass through unchanged.
 *
 * @param {Array<Object>} documents
 * @returns {Array<Object>}
 */
export const keepLatestIdCardPerSide = (documents) => {
  if (!Array.isArray(documents) || documents.length === 0) return documents;

  const getType = (doc) => doc.document_type || doc.approvalType || doc.documentType;
  const getStaff = (doc) => String(doc.staff_id || doc.employee_staff_id || doc.staffId || '');
  const getId = (doc) => {
    const raw = Number(doc.id);
    return Number.isFinite(raw) ? raw : 0;
  };
  const getStatus = (doc) => String(doc.status || '').toLowerCase();

  const winners = new Map();
  documents.forEach((doc) => {
    const type = getType(doc);
    if (!SINGLETON_ID_CARD_TYPES.has(type)) return;
    if (HIDDEN_ID_CARD_STATUSES.has(getStatus(doc))) return;
    const key = `${getStaff(doc)}:${type}`;
    const current = winners.get(key);
    if (!current || getId(doc) > getId(current)) {
      winners.set(key, doc);
    }
  });

  const seen = new Set();
  return documents.filter((doc) => {
    const type = getType(doc);
    if (!SINGLETON_ID_CARD_TYPES.has(type)) return true;
    const key = `${getStaff(doc)}:${type}`;
    const winner = winners.get(key);
    if (!winner) return false;
    if (getId(doc) !== getId(winner) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Normalize a document using its type configuration
 * @param {Object} doc - Raw document data from API
 * @returns {Object} Normalized document object
 */
export const normalizeDocument = (doc) => {
  if (!doc) return null;
  
  const docType = doc.document_type || doc.approvalType;
  const config = documentTypeConfig[docType];
  
  if (config && config.normalize) {
    return config.normalize(doc);
  }
  
  // Fallback normalization for unknown types
  return {
    id: doc.id,
    name: doc.document_info?.filename?.split('/')?.pop() || doc.document_info?.filename || `document_${doc.id}`,
    category: doc.category || 'other',
    status: doc.status || 'uploaded',
    uploadedAt: doc.document_info?.uploaded_at || doc.submitted_at || null,
    size: 0,
    filePath: doc.document_info?.filename || null,
    approvalType: docType || 'unknown',
    ocrSummary: doc.ocr_summary || {},
    rejectionReason: doc.rejection_reason || '',
  };
};

/**
 * Mirrors staff_core.services.document_verification_queue.
 * Education / professional / bank drafts stay employee-owned until submit.
 */
export const HR_QUEUE_SUBMITTED_ONLY_DOCUMENT_TYPES = new Set([
  'education_certificate',
  'professional_certificate',
  'bank_statement',
]);

/**
 * Upload is the HR handoff. Closing the review modal must not hide these
 * from Pending Verification.
 */
export const HR_QUEUE_UPLOAD_HANDOFF_DOCUMENT_TYPES = new Set([
  'birth_certificate',
  'marriage_certificate',
  'interim_birth_notification',
  'driving_license',
  'id_card_front',
  'id_card_back',
  'passport',
]);

const EMPLOYEE_CONFIRM_DOCUMENT_TYPES = new Set([
  ...HR_QUEUE_SUBMITTED_ONLY_DOCUMENT_TYPES,
]);

const HANDED_OFF_DOCUMENT_STATUSES = new Set([
  'submitted',
  'approved',
  'verified',
  'rejected',
]);

/**
 * Map a DocumentApproval status for HR's employee Documents tab.
 *
 * @param {string} rawStatus
 * @param {string} documentType
 * @returns {string}
 */
export const mapHrDocumentListStatus = (rawStatus, documentType) => {
  if (!rawStatus) return 'pending_verification';
  const s = String(rawStatus).toLowerCase();
  if (s === 'verified' || s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  if (s === 'processing' || s === 'processing_ocr') return 'processing';
  if (s === 'submitted') return 'pending_verification';
  if (s === 'pending' || s === 'ocr_completed') {
    if (HR_QUEUE_SUBMITTED_ONLY_DOCUMENT_TYPES.has(documentType)) {
      return 'awaiting_employee';
    }
    return 'pending_verification';
  }
  return s;
};

/**
 * True when HR should treat the file as handed over.
 * Family and identity uploads count from upload; education / professional /
 * bank count only after confirm-and-send.
 *
 * @param {string} rawStatus
 * @param {string} [documentType]
 * @returns {boolean}
 */
export const isDocumentSubmittedToHr = (rawStatus, documentType) => {
  const s = String(rawStatus || '').toLowerCase();
  if (HANDED_OFF_DOCUMENT_STATUSES.has(s)) return true;
  return (
    (s === 'pending' || s === 'ocr_completed')
    && HR_QUEUE_UPLOAD_HANDOFF_DOCUMENT_TYPES.has(documentType)
  );
};

/**
 * Employee-facing status for a document row / dropzone chip.
 *
 * @param {string} rawStatus
 * @param {string} documentType
 * @returns {{ status: string, label?: string }}
 */
export const getEmployeeDocumentListStatus = (rawStatus, documentType) => {
  const s = (rawStatus || '').toString().toLowerCase();
  if (!s) {
    if (EMPLOYEE_CONFIRM_DOCUMENT_TYPES.has(documentType)) {
      return { status: 'confirm_and_send' };
    }
    if (HR_QUEUE_UPLOAD_HANDOFF_DOCUMENT_TYPES.has(documentType)) {
      return { status: 'pending_hr' };
    }
    return { status: 'pending_review' };
  }
  if (s === 'on_file') return { status: 'on_file' };
  if (s === 'processing_ocr' || (s.includes('process') && !s.includes('ocr_completed'))) {
    return { status: 'processing' };
  }
  if (
    s === 'ocr_completed'
    || s === 'pending'
    || (s.includes('pending') && !s.includes('hr'))
  ) {
    if (EMPLOYEE_CONFIRM_DOCUMENT_TYPES.has(documentType)) {
      return { status: 'confirm_and_send' };
    }
    if (HR_QUEUE_UPLOAD_HANDOFF_DOCUMENT_TYPES.has(documentType)) {
      return { status: 'pending_hr' };
    }
    return { status: 'pending_review' };
  }
  if (s === 'submitted' || s.includes('submitted')) return { status: 'pending_hr' };
  if (s.includes('review') || s.includes('under_review')) return { status: 'under_review' };
  if (s.includes('awaiting') || s.includes('waiting')) return { status: 'awaiting_approval' };
  if (s.includes('approved') || s.includes('verified') || s.includes('validated') || s.includes('accepted')) {
    return { status: 'verified' };
  }
  if (s.includes('completed') || s.includes('success')) {
    return { status: s.includes('ocr') ? 'confirm_and_send' : 'completed' };
  }
  if (s.includes('fail') || s.includes('reject')) return { status: 'rejected' };
  if (s.includes('invalid') || s.includes('error')) return { status: 'invalid' };
  if (s.includes('expired') || s.includes('expiry')) return { status: 'expired' };
  if (s.includes('superseded')) return { status: 'superseded' };
  if (s.includes('uploaded') || s.includes('new')) return { status: 'new_upload' };
  if (s.includes('draft') || s.includes('saved')) return { status: 'draft' };
  if (s.includes('archived') || s.includes('deleted')) return { status: 'archived' };
  return { status: 'pending_review' };
};

const REPLACEABLE_DOCUMENT_TYPES = new Set([
  'driving_license',
  'passport',
  'id_card_front',
  'id_card_back',
  'bank_statement',
]);

const DEAD_REPLACEMENT_STATUSES = new Set([
  'rejected',
  'invalid',
  'expired',
  'archived',
  'superseded',
]);

const getDocumentTypeKey = (doc) => doc?.document_type || doc?.approvalType || doc?.documentType;

const getUploadedMs = (doc) => {
  const t = doc?.uploadedAt ? new Date(doc.uploadedAt).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

const getNumericId = (doc) => {
  const n = Number(doc?.id);
  return Number.isFinite(n) ? n : 0;
};

/**
 * True when a later live upload of the same singleton-style document exists.
 * Used to de-emphasise a rejected card after the employee has re-uploaded.
 *
 * @param {Object} doc
 * @param {Array<Object>} [allDocs]
 * @returns {boolean}
 */
export const hasLaterReplacementUpload = (doc, allDocs = []) => {
  const type = getDocumentTypeKey(doc);
  if (!REPLACEABLE_DOCUMENT_TYPES.has(type)) return false;

  const uploaded = getUploadedMs(doc);
  const id = getNumericId(doc);

  return allDocs.some((other) => {
    if (!other || other.id === doc.id) return false;
    if (getDocumentTypeKey(other) !== type) return false;
    const otherStatus = String(other.status || '').toLowerCase();
    if (DEAD_REPLACEMENT_STATUSES.has(otherStatus) || otherStatus.includes('reject')) {
      return false;
    }
    const otherUploaded = getUploadedMs(other);
    if (otherUploaded > uploaded) return true;
    if (otherUploaded === uploaded && getNumericId(other) > id) return true;
    return false;
  });
};
